<template>
    <v-container class="secondary-font pa-0" style="font-size: 1rem;">
        <transition name="slide-from-left" mode="out-in">
            <active-players v-if="showActivePlayers" key="active-players" @setShowScreen="$emit('setShowScreen', 'live-score')" />
            <div v-else-if="current" :key="current.key" class="live-card-row">
                <team-stat-component
                    v-if="current.type === 'team' && SHOT_TYPES.includes(current.statType)"
                    :match="current.data"
                    :type="current.statType"
                />
                <compare-stat-component
                    v-else-if="current.type === 'team'"
                    :match="current.data"
                    :type="current.statType"
                />
                <player-stat-component
                    v-else
                    :type="current.statType"
                    :player="current.data"
                />
                <!-- a sponsor of the league next to every card -->
                <img :src="current.sponsor" alt="" class="live-sponsor">
            </div>
        </transition>
    </v-container>
</template>

<script>
    import TeamStatComponent from '@/components/TeamStatComponent'
    import PlayerStatComponent from '@/components/PlayerStatComponent'
    import CompareStatComponent from '@/components/CompareStatComponent'
    import ActivePlayers from '@/components/ActivePlayers.vue'
    import { subscribeLiveEvents } from '@/utils/liveEvents'

    const SHOT_TYPES = ['2P', '3P', '1P']
    // each card stays this long
    const DISPLAY_MS = 5000
    // a card that waited longer than this is out of date and skipped, so the overlay never lags the game
    const MAX_AGE_MS = 10000
    // at most this many cards wait; a burst (e.g. the stats operator correcting) keeps its latest ones
    const MAX_QUEUE = 4

    export default {
        components: {
            TeamStatComponent,
            PlayerStatComponent,
            CompareStatComponent,
            ActivePlayers
        },
        props: {
            id: {
                type: String,
                required: true
            },
            showActivePlayers: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                SHOT_TYPES,
                // the card on screen and the ones waiting: { type, statType, data, key, receivedAt }
                current: null,
                queue: [],
                eventSeq: 0,
                advanceTimer: null,
                unsubscribe: null,
                sponsors: [
                    require('@/assets/sportsiq.jpg'),
                    require('@/assets/gsa.jpg'),
                    require('@/assets/molten.png'),
                    require('@/assets/IOLI_logo GR.jpg')
                ]
            }
        },
        mounted() {
            this.unsubscribe = subscribeLiveEvents(this.id, {
                player: data => this.enqueue('player', data),
                team: data => this.enqueue('team', data)
            })
        },
        beforeDestroy() {
            if (this.unsubscribe) this.unsubscribe()
            clearTimeout(this.advanceTimer)
        },
        methods: {
            // Players: the shots only. Teams: every stat with a card
            enqueue(type, data) {
                const statType = type === 'player' ? this.getPlayerEventType(data) : this.getTeamEventType(data)
                if (type === 'player' ? !SHOT_TYPES.includes(statType) : !statType) return
                const sponsor = this.sponsors[Math.floor(Math.random() * this.sponsors.length)]
                this.queue.push({ type, statType, data, sponsor, key: ++this.eventSeq, receivedAt: Date.now() })
                if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE)
                if (!this.current) this.advance()
            },
            // The next card that is still current, each one on screen for DISPLAY_MS
            advance() {
                clearTimeout(this.advanceTimer)
                const now = Date.now()
                while (this.queue.length && now - this.queue[0].receivedAt > MAX_AGE_MS) {
                    this.queue.shift()
                }
                this.current = this.queue.shift() || null
                this.advanceTimer = this.current ? setTimeout(this.advance, DISPLAY_MS) : null
            },
            getTeamEventType(teamEvent) {
                if (!teamEvent) return ''
                if (teamEvent.team1.twoPointsMade > 0 || teamEvent.team1.twoPointAttempts > 0 || teamEvent.team2.twoPointsMade > 0 || teamEvent.team2.twoPointAttempts > 0) {
                    return '2P'
                } else if (teamEvent.team1.threePointsMade > 0 || teamEvent.team1.threePointAttempts > 0 || teamEvent.team2.threePointsMade > 0 || teamEvent.team2.threePointAttempts > 0) {
                    return '3P'
                } else if (teamEvent.team1.freeThrowsMade > 0 || teamEvent.team1.freeThrowAttempts > 0 || teamEvent.team2.freeThrowsMade > 0 || teamEvent.team2.freeThrowAttempts > 0) {
                    return '1P'
                } else if (teamEvent.team1.offensiveRebounds > 0 || teamEvent.team2.offensiveRebounds > 0) {
                    return 'T.R.'
                } else if (teamEvent.team1.defensiveRebounds > 0 || teamEvent.team2.defensiveRebounds > 0) {
                    return 'T.R.'
                } else if (teamEvent.team1.assists > 0 || teamEvent.team2.assists > 0) {
                    return 'AST'
                } else if (teamEvent.team1.steals > 0 || teamEvent.team2.steals > 0) {
                    return 'STL'
                } else if (teamEvent.team1.turnovers > 0 || teamEvent.team2.turnovers > 0) {
                    return 'TO'
                } else if (teamEvent.team1.blockedShots > 0 || teamEvent.team2.blockedShots > 0) {
                    return 'BLK'
                }
                return ''
            },
            getPlayerEventType(playerEvent) {
                if (!playerEvent) return ''
                if (playerEvent.twoPointsMade > 0 || playerEvent.twoPointAttempts > 0) {
                    return '2P'
                } else if (playerEvent.threePointsMade > 0 || playerEvent.threePointAttempts > 0) {
                    return '3P'
                } else if (playerEvent.freeThrowsMade > 0 || playerEvent.freeThrowAttempts > 0) {
                    return '1P'
                } else if (playerEvent.rebounds > 0) {
                    return 'rebounds'
                } else if (playerEvent.assists > 0) {
                    return 'assists'
                } else if (playerEvent.steals > 0) {
                    return 'steals'
                } else if (playerEvent.turnovers > 0) {
                    return 'turnovers'
                } else if (playerEvent.blockedShots > 0) {
                    return 'blockedShots'
                }
                return ''
            }
        }
    }
</script>

<style>
    .v-progress-circular__info {
        display: block;
    }

    /* the card and its sponsor side by side, whatever the width of the screen */
    .live-card-row {
        display: flex;
        align-items: flex-end;
    }

    .live-card-row > .live-card {
        flex: 0 0 auto;
    }

    /* the same box for every sponsor, 12px after the card */
    .live-sponsor {
        flex: 0 0 auto;
        margin-left: 12px;
        width: 400px;
        height: 200px;
        border-radius: 8px;
        background: #fff;
        object-fit: contain;
    }
</style>
